import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { setInterval } from '../../../../actions/graphsFilterActions';
import IrrigationChart from '../../../../components/Highcharts/IrrigationChart/IrrigationChart';
import StackedSoilMoistureChart from '../../../../components/Highcharts/LineCharts/StackedSoilMoistureChart/StackedSoilMoistureChart';
import SoilMoistureChart from '../../../../components/Highcharts/LineCharts/SoilMoistureChart/SoilMoistureChart';
import RootsChart from '../../../../components/Highcharts/RootsChart/RootsChart';
import PlantsChart from '../../../../components/Highcharts/LineCharts/PlantsChart/PlantsChart';
import RainfallChart from '../../../../components/Highcharts/RainfallChart/RainfallChart';
import HumidityTemperatureChart from '../../../../components/Highcharts/LineCharts/HumidityTemperatureChart/HumidityTemperatureChart';
import SalinityChart from '../../../../components/Highcharts/LineCharts/SalinityChart/SalinityChart';
import SoilTemperatureChart from '../../../../components/Highcharts/LineCharts/SoilTemperatureChart/SoilTemperatureChart';
import ColdHoursChart from '../../../../components/Highcharts/LineCharts/ColdHoursChart/ColdHoursChart';
import DegreeDayChart from '../../../../components/Highcharts/DegreeDayChart/DegreeDayChart';
import { DEFAULT_COLD_HOURS_CONFIG } from '../../../../components/Highcharts/LineCharts/ColdHoursChart/types';
import {
  CHART_TITLE_BW_KC_MM,
  CHART_TITLE_SECTOR_HUMEDAD_SUELO,
  CHART_TITLE_BW_SUMATORIA_HUMEDAD,
  CHART_TITLE_BW_PROFUNDIDAD_RAICES,
  CHART_TITLE_BW_DENDROMETRO,
  CHART_TITLE_BW_HUMEDAD_TEMPERATURA,
  CHART_TITLE_BW_SALINIDAD,
  CHART_TITLE_BW_TEMPERATURA_SUELO,
  CHART_TITLE_BW_HORAS_FRIO,
  CHART_TITLE_BW_GRADOS_DIA,
} from '../../../../constants/graphs';

interface AllChartsGridProps {
  sectorId: string;
  farmId: string;
  selectedCharts?: string[];
  startDate: string;
  endDate: string;
  isGeneratingPDF?: boolean;
}

const AllChartsGrid: React.FC<AllChartsGridProps> = ({
  sectorId,
  farmId,
  selectedCharts = [
    'irrigation',
    'stackedSoilMoisture',
    'soilMoisture',
    'roots',
    'plants',
    'rainfall',
    'humidityTemperature',
    'salinity',
    'soilTemperature',
    'coldHours',
    'degreeDay',
  ],
  startDate,
  endDate,
  isGeneratingPDF = false,
}) => {
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const formattedStartDate = dayjs(startDate).utc().startOf('day').format('YYYY-MM-DD');
    const formattedEndDate = dayjs(endDate).utc().endOf('day').format('YYYY-MM-DD');

    dispatch(setInterval({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    }));

    // Allow time for the interval to be properly set before rendering charts
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 150);

    return () => {
      clearTimeout(timer);
      setIsReady(false);

      const currentDate = dayjs();
      const defaultEndDate = currentDate.endOf('day').format('YYYY-MM-DD');
      const defaultStartDate = currentDate.subtract(14, 'day').startOf('day').format('YYYY-MM-DD');

      dispatch(setInterval({
        startDate: defaultStartDate,
        endDate: defaultEndDate,
      }));
    };
  }, [dispatch, startDate, endDate]);

  if (!isReady) {
    return null;
  }

  const renderChart = (chartId: string) => {
    if (!selectedCharts.includes(chartId)) {
      return null;
    }

    switch (chartId) {
      case 'irrigation':
        return (
          <IrrigationChart
            key="irrigation"
            chartView="dashboard"
            hideLegend={false}
            hideYValues={false}
            hideXValues={false}
            sectorId={sectorId}
            farmId={farmId}
            height="60px"
            yAxisTitle=""
            title={CHART_TITLE_BW_KC_MM}
            labelYPosition={45}
          />
        );

      case 'stackedSoilMoisture':
        return (
          <StackedSoilMoistureChart
            key="stackedSoilMoisture"
            chartView="dashboard"
            hideLegend={false}
            hideXValues={false}
            hideYValues={false}
            sectorId={sectorId}
            farmId={farmId}
            height={200}
            title={CHART_TITLE_SECTOR_HUMEDAD_SUELO}
            yAxisTitle="%"
            isGeneratingPDF={isGeneratingPDF}
          />
        );

      case 'soilMoisture':
        return (
          <SoilMoistureChart
            key="soilMoisture"
            chartView="dashboard"
            hideYValues={false}
            hideXValues={false}
            hideLegend={true}
            sectorId={sectorId}
            farmId={farmId}
            height={200}
            title={CHART_TITLE_BW_SUMATORIA_HUMEDAD}
            yAxisTitle="%"
          />
        );

      case 'roots':
        return (
          <RootsChart
            key="roots"
            chartView="dashboard"
            hideLegend={false}
            hideXValues={false}
            hideYValues={false}
            sectorId={sectorId}
            farmId={farmId}
            height={80}
            yAxisTitle="cm"
            title={CHART_TITLE_BW_PROFUNDIDAD_RAICES}
          />
        );

      case 'plants':
        return (
          <PlantsChart
            key="plants"
            chartView="dashboard"
            hideYValues={false}
            hideXValues={false}
            sectorId={sectorId}
            farmId={farmId}
            height={120}
            title={CHART_TITLE_BW_DENDROMETRO}
            yAxisTitle="μm"
          />
        );

      case 'rainfall':
        return (
          <RainfallChart
            key="rainfall"
            chartView="dashboard"
            sectorId={sectorId}
            farmId={farmId}
            height={60}
          />
        );

      case 'humidityTemperature':
        return (
          <HumidityTemperatureChart
            key="humidityTemperature"
            chartView="dashboard"
            sectorId={sectorId}
            farmId={farmId}
            height={140}
            hideXValues={false}
            hideRightYAxisTitle={true}
            title={CHART_TITLE_BW_HUMEDAD_TEMPERATURA}
          />
        );

      case 'salinity':
        return (
          <SalinityChart
            key="salinity"
            chartView="dashboard"
            sectorId={sectorId}
            farmId={farmId}
            height={120}
            hideXValues={false}
            isGeneratingPDF={isGeneratingPDF}
            title={CHART_TITLE_BW_SALINIDAD}
            yAxisTitle="VIC"
          />
        );

      case 'soilTemperature':
        return (
          <SoilTemperatureChart
            key="soilTemperature"
            chartView="dashboard"
            sectorId={sectorId}
            farmId={farmId}
            height={120}
            hideXValues={false}
            isGeneratingPDF={isGeneratingPDF}
            title={CHART_TITLE_BW_TEMPERATURA_SUELO}
            yAxisTitle="ºC"
          />
        );

      case 'coldHours':
        return (
          <ColdHoursChart
            key="coldHours"
            chartView="dashboard"
            sectorId={sectorId}
            farmId={farmId}
            height={100}
            config={DEFAULT_COLD_HOURS_CONFIG}
            hideXValues={false}
            hideRightYAxisTitle={true}
            title={CHART_TITLE_BW_HORAS_FRIO}
            yAxisTitle="Horas"
          />
        );

      case 'degreeDay':
        return (
          <DegreeDayChart
            key="degreeDay"
            chartView="dashboard"
            sectorId={sectorId}
            farmId={farmId}
            height={80}
            hideXValues={false}
            title={CHART_TITLE_BW_GRADOS_DIA}
          />
        );

      default:
        return null;
    }
  };

  const chartOrder = [
    'irrigation',
    'stackedSoilMoisture',
    'soilMoisture',
    'roots',
    'plants',
    'rainfall',
    'humidityTemperature',
    'salinity',
    'soilTemperature',
    'coldHours',
    'degreeDay',
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {chartOrder.map((chartId) => renderChart(chartId)).filter(Boolean)}
    </Box>
  );
};

export default AllChartsGrid;
